import { useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import { PostHogInstance } from 'shared/posthogCapture/types';
import { FormOrigin } from 'form/types';

interface TemplateContext {
  templateId: string;
  templateName: string;
}

interface TemplateIdContext {
  templateId: string;
}

interface TemplateContextWithOrigin extends TemplateContext {
  origin: FormOrigin;
}

interface SubmitErrorContext extends TemplateContext {
  error?: string;
}

interface SmartActionDownloadContext {
  templateName: string;
  isFilled: boolean;
}

export const createFormCaptureEvents = (posthog: PostHogInstance) => ({
  pageViewed: () => {
    posthog?.capture('form / page / viewed');
  },
  editViewOpened: (context: TemplateContextWithOrigin) => {
    posthog?.capture('form / edit / viewed', context);
  },
  completedViewOpened: (context: TemplateContextWithOrigin) => {
    posthog?.capture('form / completed / viewed', context);
  },
  formSubmitted: (context: TemplateContext) => {
    posthog?.capture('form / edit / submit / selected', context);
  },
  formSubmitError: (context: SubmitErrorContext) => {
    posthog?.capture('form / edit / submit / error', context);
  },
  editCancelled: (context: TemplateContext) => {
    posthog?.capture('form / edit / cancelled', context);
  },
  completedEditSelected: (context: TemplateContext) => {
    posthog?.capture('form / completed / edit / selected', context);
  },
  downloadClicked: (context: TemplateIdContext) => {
    posthog?.capture('form / completed / download / selected', context);
  },
  smartActionDownloadSelected: (context: SmartActionDownloadContext) => {
    posthog?.capture(
      'form / entry / smart action / download / selected',
      context
    );
  },
});

export type FormCaptureEvents = ReturnType<typeof createFormCaptureEvents>;

export const usePosthogCapture = () => {
  const posthog = usePostHog();
  return useMemo(() => createFormCaptureEvents(posthog), [posthog]);
};
